import React, { useContext, useEffect } from 'react';
import { NextPageContext } from 'next';
import { appAuthContext } from 'context/AuthContext';
import JsonLd from 'components/JsonLd/JsonLd';
import { buildQuestionPageJsonLd } from 'utils/seo/jsonld';
import clickStreamPageview from 'utils/clickstream/useClickStreamPageview';
import { getLayout } from 'components/Layout/Layout';
import BackgroundWrapper from 'components/BackgroundWrapper';
import Question from 'views/Questions';
import getQuerySSR from 'utils/getQuerySSR';
import getQuestionData from 'views/Questions/api/getQuestionData';
import { QuestionData } from 'views/Questions/Question.types';
import getUserParallel from 'views/Questions/api/getUserParallel';
import checkAuthFromHeaders from 'utils/checkAuthFromHeaders';
import { Api } from 'api/otvety';
import { marusiaServerApi } from 'api';
import { logInfo } from 'utils/logging';

type QuestionPageProps = {
  data: QuestionData;
  userParallel: number | null;
  marusiaAnswer?: Api.GetMarusiaData200Response.MarusiaAnswer;
};

const QuestionPage = ({ data, userParallel, marusiaAnswer }: QuestionPageProps) => {
  const { user } = useContext(appAuthContext);

  useEffect(() => {
    clickStreamPageview({
      id: user?.data?.attributes?.foreign_id,
      userType: user?.data?.attributes?.type,
      cd2: data.question.grade.toString() || '',
      cd3: JSON.stringify({ marusia: !!marusiaAnswer }),
    });
  }, [data.question.grade, marusiaAnswer, user?.data?.attributes?.foreign_id]);

  const questionJsonLd = buildQuestionPageJsonLd(
    data.question,
    data.answers,
    marusiaAnswer,
  );

  return (
    <>
      <BackgroundWrapper>
        <Question data={data} userParallel={userParallel} marusiaAnswer={marusiaAnswer} />
      </BackgroundWrapper>
      <JsonLd id="questionJsonLd" data={questionJsonLd} />
    </>
  );
};

QuestionPage.getLayout = getLayout;

export async function getServerSideProps(params: NextPageContext) {
  const slug = getQuerySSR(params.query?.slug);

  const [data, marusiaAnswer] = await Promise.all([
    getQuestionData(params, slug),
    marusiaServerApi(params)
      .getMarusiaData(slug || '')
      .catch((error) => logInfo(error, true)),
  ]);

  if (!data) {
    return {
      redirect: {
        permanent: false,
        destination: '/404',
      },
      props: {},
    };
  }

  const userAuth = checkAuthFromHeaders(params);

  let userParallel;
  if (userAuth) {
    userParallel = await getUserParallel(params?.req?.headers || {});
  }

  return {
    props: {
      data: data,
      userParallel: userParallel || null,
      title: `💯 ${data.question.pageTitle}`,
      description: data.question.pageDescription,
      marusiaAnswer: marusiaAnswer?.marusia_answer || null,
    },
  };
}

export default QuestionPage;
